import { devLog } from './devLog';
import { purgeLegacyAuthStorage } from './authSession';

/** sessionStorage key for one-shot landing-page messaging (no URL params). */
export const AUTH_REDIRECT_REASON_KEY = 'auth_redirect_reason';

/**
 * Synchronous cross-module guard (AuthContext timer, api.js 401, route guards).
 * Resets on full navigation (new document) — dedupes concurrent callers within one load only.
 */
export const redirectInProgress = { current: false };

function persistAuthRedirectReason(reason) {
  if (reason !== 'session_expired') {
    return;
  }
  try {
    sessionStorage.setItem(AUTH_REDIRECT_REASON_KEY, reason);
  } catch {
    /* private browsing / blocked storage */
  }
}

/**
 * Read and clear the one-shot redirect reason (call once on landing mount).
 * @returns {string | null}
 */
export function consumeAuthRedirectReason() {
  try {
    const reason = sessionStorage.getItem(AUTH_REDIRECT_REASON_KEY);
    if (reason !== null) {
      sessionStorage.removeItem(AUTH_REDIRECT_REASON_KEY);
    }
    return reason;
  } catch {
    return null;
  }
}

/** @returns {boolean} True while a hard login redirect is in flight. */
export function isAuthRedirectInProgress() {
  return redirectInProgress.current;
}

/**
 * Single entry for session-expiry redirects; dedupes concurrent callers
 * (AuthContext proactive timer, api.js 401, route guards).
 * @param {{ reason?: string }} [options]
 * @returns {boolean} False when a redirect was already in progress.
 */
export function notifySessionExpired({ reason = 'session_expired' } = {}) {
  if (redirectInProgress.current) {
    return false;
  }
  redirectInProgress.current = true;
  if (reason) {
    devLog.warn('Auth redirect:', reason);
  }
  persistAuthRedirectReason('session_expired');
  purgeLegacyAuthStorage();
  window.location.replace('/');
  return true;
}

/**
 * Hard redirect to login without updating React auth state (avoids protected-route blink).
 * @param {{ reason?: string }} [options]
 */
export function redirectToLogin({ reason } = {}) {
  if (redirectInProgress.current) {
    return;
  }
  redirectInProgress.current = true;

  if (reason) {
    devLog.warn('Auth redirect:', reason);
  }

  persistAuthRedirectReason(reason);
  purgeLegacyAuthStorage();
  window.location.replace('/');
}
